import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Button,
  InputAdornment,
  MenuItem,
  makeStyles
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'

const BlurField = ({ value, onCommit, ...props }) => {
  const [local, setLocal] = useState(value ?? '')
  useEffect(() => { setLocal(value ?? '') }, [value])
  return (
    <TextField
      {...props}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value ?? '')) onCommit(local) }}
    />
  )
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  group: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`
  },
  groupLabel: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: theme.spacing(1),
    '&:last-child': { marginBottom: 0 }
  },
  resetBtn: {
    padding: 4,
    opacity: 0.5,
    '&:hover': { opacity: 1 }
  },
  resetAll: {
    marginTop: theme.spacing(1),
    alignSelf: 'flex-end'
  }
}))

const GROUPS = ['Labor Rates', 'Specifications', 'Warranties & Periods', 'Frequencies & Company']

const VariablesEditor = ({ variableDefinitions, resolvedVariables, onVariableChange, onResetVariables, proposalVariables }) => {
  const classes = useStyles()

  if (!variableDefinitions || !variableDefinitions.length) return null

  const grouped = {}
  GROUPS.forEach(g => { grouped[g] = [] })
  variableDefinitions.forEach(def => {
    if (grouped[def.group]) grouped[def.group].push(def)
  })

  const hasOverrides = proposalVariables && Object.keys(proposalVariables).length > 0

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle2">Proposal Variables</Typography>
      <Typography variant="caption" color="textSecondary">
        These values are used in service descriptions and terms via {'{{placeholders}}'}. Edit here to change everywhere.
      </Typography>

      {GROUPS.map(groupName => {
        const defs = grouped[groupName]
        if (!defs || !defs.length) return null
        return (
          <Box key={groupName} className={classes.group}>
            <Typography className={classes.groupLabel}>{groupName}</Typography>
            {defs.map(def => {
              const currentVal = resolvedVariables?.[def.key] ?? def.defaultValue
              const isOverridden = proposalVariables && proposalVariables[def.key] !== undefined

              return (
                <Box key={def.key} className={classes.fieldRow}>
                  {def.type === 'select' ? (
                    <TextField
                      select
                      label={def.label}
                      value={currentVal}
                      onChange={(e) => onVariableChange(def.key, e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                    >
                      {(def.options || []).map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : def.type === 'text' ? (
                    <BlurField
                      label={def.label}
                      value={currentVal}
                      onCommit={(val) => onVariableChange(def.key, val)}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <TextField
                      label={def.label}
                      value={currentVal}
                      onChange={(e) => {
                        let val = e.target.value
                        if (def.type === 'number' || def.type === 'currency') {
                          val = val === '' ? '' : Number(val)
                        }
                        onVariableChange(def.key, val)
                      }}
                      type="number"
                      size="small"
                      variant="outlined"
                      fullWidth
                      InputProps={def.type === 'currency' ? {
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      } : undefined}
                      inputProps={{ step: def.type === 'currency' ? '0.01' : '1' }}
                    />
                  )}
                  {isOverridden && (
                    <Tooltip title="Reset to default">
                      <IconButton
                        className={classes.resetBtn}
                        size="small"
                        onClick={() => onVariableChange(def.key, undefined)}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )
            })}
          </Box>
        )
      })}

      {hasOverrides && (
        <Button
          className={classes.resetAll}
          size="small"
          variant="outlined"
          onClick={onResetVariables}
        >
          Reset All to Defaults
        </Button>
      )}
    </Box>
  )
}

export default VariablesEditor
